import Vue from 'vue'
import * as Sentry from '@sentry/browser'
import * as Integrations from '@sentry/integrations'
import VueGtag from 'vue-gtag'
import InputTag from 'vue-input-tag'
import VueSmoothScroll from 'vue-smooth-scroll'

import App from '@/App.vue'
import router from '@/router'
import store from '@/store'
import * as types from '@/store/types'
import api from '@/constants/api.urls'
import FontAwesomeIcon from '@/plugins/fontawesome'
import ElementUiPlugin from '@/plugins/element'
import i18n from '@/plugins/i18n'
import apiService from '@/helpers/apiService'
import { toFixed } from '@/utils/common'

import VButton from '@/components/common/VButton.vue'

import '@/assets/style/scss/base.scss'

import packageJson from '../package.json'

Vue.config.productionTip = false

const isProduction = process.env.NODE_ENV === 'production'

if (isProduction && process.env.VUE_APP_SENTRY_DSN) {
  const dsn = process.env.VUE_APP_SENTRY_DSN
  Sentry.init({
    dsn,
    release: `${packageJson.name}@${packageJson.version}`,
    integrations: [
      new Integrations.Vue({
        Vue,
        attachProps: true
      })
    ],
    beforeSend(event) {
      // eslint-disable-next-line no-param-reassign
      event.extra = {
        ...(event.extra || {}),
        userDirectLogin: store.state[types.SESSION].model.user.directLogin
      }
      return event
    }
  })
}

Vue.component('font-awesome-icon', FontAwesomeIcon)
Vue.component('input-tag', InputTag)
Vue.component(VButton.name, VButton)

Vue.use(ElementUiPlugin)
Vue.use(VueGtag, {
  config: {
    id: 'G-T4LVEB388X',
    enabled: isProduction
  }
})
Vue.use(VueSmoothScroll)
Vue.filter('toFixed', toFixed)

apiService.client.interceptors.request.use(
  (config) => {
    const { isViewSession } = store.getters
    const { currentlyViewProfile, firstLogin } = store.state[types.SESSION].model

    const viewApiMap = {
      [api.summaryData]: api.viewSummaryData,
      [api.userProfileInfoData]: api.viewUserProfileInfoData,
      [api.placements]: api.viewPlacements,
      [api.disputedPlacements]: api.viewDisputedPlacements,
      [api.bestPlacements]: api.viewBestPlacements,
      [api.worstPlacements]: api.viewWorstPlacements,
      [api.blackListPlacements]: api.viewBlackListPlacements,
      [api.campaigns]: api.viewCampaigns,
      [api.whiteListPlacements]: api.viewWhiteListPlacements,
      [api.keywords]: api.viewKeywords,
      [api.exceptionKeywords]: api.viewExceptionKeywords,
      [api.ads]: api.viewAds,
      [api.exceptionAds]: api.viewExceptionAds,
      [api.accountSettings]: api.viewAccountSettings,
      [api.log]: api.viewLog
    }

    const currentUrl = config.url
    const currentParams = config.params || {}
    const viewUrl = viewApiMap[currentUrl] || currentUrl.replace(api.log, api.viewLog)
    const viewParams = { account_id: currentlyViewProfile }

    if (firstLogin) {
      viewParams.first_login = firstLogin
    }
    return {
      ...config,
      url: isViewSession ? viewUrl : currentUrl,
      params: { ...currentParams, ...viewParams },
      headers: {
        ...config.headers,
        ...store.getters.headers
      }
    }
  },
  error => error
)
apiService.client.interceptors.response.use(
  response => (response && response.data ? response.data : response) || {},
  (error) => {
    // Do something with response error
    if (apiService.isCancel(error)) {
      return Promise.reject(error)
    }

    const status = error.response ? error.response.status : 408
    const { baseURL = '', url = '' } = error.config
    const urlPath = url.replace(baseURL, '')

    if (status === 302) {
      if (urlPath === api.accounts) {
        return Promise.resolve(error.response.data)
      }
    } else if (status === 400) {
      if (urlPath === api.refreshToken) {
        store.dispatch(types.REMOVE_SESSION).then(() => router.push({ name: 'home' }))
      }
    } else if (status === 401) {
      if (!error.config.retry && urlPath !== api.refreshToken) {
        return store.dispatch(types.REFRESH_SESSION).then(() => apiService.client({
          ...error.config,
          headers: {
            ...error.config.headers,
            ...store.getters.headers
          },
          retry: true
        }))
      }

      store
        .dispatch(types.REMOVE_SESSION, { status: 401 })
        .then(() => router.push({ name: 'home' }))
    } else if (status === 409) {
      store
        .dispatch(types.REMOVE_SESSION, { status: 409 })
        .then(() => router.push({ name: 'logout' }))
        .then(() => store.dispatch(types.SET_LOCAL_PROMPT, { value: 'consent' }))
    } else if (status === 424) {
      router.push({ name: 'select-account' })
    }

    console.warn('interceptors error', status, error.response, error)
    return Promise.reject(error)
  }
)

new Vue({
  router,
  store,
  i18n,
  render: h => h(App)
}).$mount('#app')
